import { useEffect, useState, type FormEvent } from "react";
import type { AuthenticatedUser, CatalogImportPreview } from "@fira/contracts";
import { OperationsPanel } from "./OperationsPanel";
import { ProductAnalysisPanel } from "./ProductAnalysisPanel";
import { ReportsPanel } from "./ReportsPanel";
import { SalesPanel } from "./SalesPanel";
import { BridgeMaintenancePanel } from "./BridgeMaintenancePanel";

interface MeResponse {
  user: AuthenticatedUser;
}

interface BackendVersion {
  environment: "LIVE" | "LOCAL";
  version: string;
  revision: string;
}

interface ApplyResult {
  importId: string;
  createdProducts: number;
  updatedProducts: number;
  unchangedProducts: number;
}

interface CatalogProduct {
  id: string;
  sourceKey: string;
  name: string;
  family: string | null;
  barcode: string | null;
  priceCents: number;
  missingFromLastImport: boolean;
}

interface CatalogProductList {
  items: CatalogProduct[];
  total: number;
  limit: number;
  query: string;
}

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    ...init,
  });
  const data = (await response.json().catch(() => null)) as
    | T
    | { message?: string; error?: string }
    | null;

  if (!response.ok) {
    const error = data as { message?: string; error?: string } | null;
    throw new Error(error?.message ?? error?.error ?? "Error de comunicació.");
  }

  return data as T;
}

function BackendTag() {
  const [backend, setBackend] = useState<BackendVersion | null>(null);

  useEffect(() => {
    void api<BackendVersion>("/api/version")
      .then(setBackend)
      .catch(() => setBackend(null));
  }, []);

  return (
    <span
      className={`backend-tag ${backend?.environment === "LOCAL" ? "backend-tag-local" : ""}`}
      title="Versió del backend publicada"
    >
      <i aria-hidden="true" />
      {backend?.environment ?? "LIVE"}
      {backend && (
        <>
          <b>v{backend.version}</b>
          <small>{backend.revision}</small>
        </>
      )}
    </span>
  );
}

function Login({ onLogin }: { onLogin: (user: AuthenticatedUser) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api<{ user: AuthenticatedUser }>(
        "/api/v1/auth/login",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ email, password, client: "WEB" }),
        },
      );
      onLogin(result.user);
    } catch (loginError) {
      setError(
        loginError instanceof Error
          ? loginError.message
          : "No s'ha pogut iniciar la sessió.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-layout">
      <section className="brand-panel">
        <div className="brand-mark" aria-hidden="true">
          <span>ES</span>
        </div>
        <div className="brand-copy">
          <p className="eyebrow">EIRA SEREN</p>
          <h1>Fira TPV</h1>
          <p>Registre privat de vendes itinerants per a fires.</p>
        </div>
      </section>
      <form className="login-card" onSubmit={submit}>
        <div className="login-heading">
          <p className="eyebrow">ADMINISTRACIÓ</p>
          <h2>Inicia la sessió</h2>
          <p>Accedeix amb les credencials privades d'Eira Seren.</p>
        </div>
        <label>
          Correu electrònic
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>
        <label>
          Contrasenya
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </label>
        {error && (
          <p className="alert error" role="alert">
            {error}
          </p>
        )}
        <button className="primary login-submit" disabled={busy}>
          {busy ? "Entrant…" : "Entrar"}
        </button>
        <p className="login-security">
          <span aria-hidden="true">●</span>
          Accés restringit · Sessió privada
        </p>
        <div className="login-version">
          <BackendTag />
        </div>
      </form>
    </main>
  );
}

function ImportPanel({ onApplied }: { onApplied: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CatalogImportPreview | null>(null);
  const [result, setResult] = useState<ApplyResult | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<"preview" | "apply" | null>(null);

  async function upload(mode: "preview" | "apply") {
    if (!file) {
      setError("Selecciona un fitxer.");
      return;
    }

    setBusy(mode);
    setError("");
    setResult(null);
    const form = new FormData();
    form.append("file", file);

    try {
      if (mode === "preview") {
        const data = await api<CatalogImportPreview>(
          "/api/v1/catalog/imports/preview",
          { method: "POST", body: form },
        );
        setPreview(data);
      } else {
        const data = await api<ApplyResult>("/api/v1/catalog/imports/apply", {
          method: "POST",
          body: form,
        });
        setResult(data);
        onApplied();
      }
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : "La importació ha fallat.",
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="panel">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">CATÀLEG</p>
          <h2>Importa el fitxer d’Àgora</h2>
        </div>
        <span className="badge">XLS HTML · XLSX</span>
      </div>

      <div className="upload-row">
        <input
          type="file"
          accept=".xls,.xlsx"
          onChange={(event) => {
            setFile(event.target.files?.[0] ?? null);
            setPreview(null);
            setResult(null);
          }}
        />
        <button
          className="secondary"
          type="button"
          disabled={!file || busy !== null}
          onClick={() => void upload("preview")}
        >
          {busy === "preview" ? "Analitzant…" : "Previsualitzar"}
        </button>
      </div>

      {error && <p className="alert error">{error}</p>}
      {result && (
        <p className="alert success">
          Catàleg aplicat: {result.createdProducts} nous,{" "}
          {result.updatedProducts} actualitzats i {result.unchangedProducts}{" "}
          sense canvis.
        </p>
      )}

      {preview && (
        <>
          <div className="metrics">
            <article>
              <span>Productes vàlids</span>
              <strong>{preview.validProducts}</strong>
            </article>
            <article>
              <span>Amb codi</span>
              <strong>{preview.productsWithBarcode}</strong>
            </article>
            <article>
              <span>Sense codi</span>
              <strong>{preview.productsWithoutBarcode}</strong>
            </article>
            <article className={preview.errors ? "metric-error" : ""}>
              <span>Errors</span>
              <strong>{preview.errors}</strong>
            </article>
          </div>

          {preview.issues.length > 0 && (
            <div className="issues">
              {preview.issues.map((issue, index) => (
                <p key={`${issue.code}-${issue.rowNumber}-${index}`}>
                  <strong>
                    {issue.severity === "ERROR" ? "Error" : "Avís"}:
                  </strong>{" "}
                  {issue.message}
                </p>
              ))}
            </div>
          )}

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>ID Àgora</th>
                  <th>Nom</th>
                  <th>Família</th>
                  <th>Barcode</th>
                  <th>Preu</th>
                </tr>
              </thead>
              <tbody>
                {preview.sample.map((product) => (
                  <tr key={product.sourceKey}>
                    <td>{product.sourceKey}</td>
                    <td>{product.name}</td>
                    <td>{product.family ?? "—"}</td>
                    <td>{product.barcode ?? "—"}</td>
                    <td>{(product.priceCents / 100).toFixed(2)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            className="primary"
            type="button"
            disabled={preview.errors > 0 || busy !== null}
            onClick={() => void upload("apply")}
          >
            {busy === "apply" ? "Aplicant…" : "Aplicar importació"}
          </button>
        </>
      )}
    </section>
  );
}

function CatalogPanel({ refreshToken }: { refreshToken: number }) {
  const [query, setQuery] = useState("");
  const [catalog, setCatalog] = useState<CatalogProductList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError("");
      void api<CatalogProductList>(
        `/api/v1/catalog/products?q=${encodeURIComponent(query)}&limit=1000`,
        { signal: controller.signal },
      )
        .then(setCatalog)
        .catch((catalogError: unknown) => {
          if (controller.signal.aborted) {
            return;
          }
          setError(
            catalogError instanceof Error
              ? catalogError.message
              : "No s'ha pogut carregar el catàleg.",
          );
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setLoading(false);
          }
        });
    }, 220);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, refreshToken]);

  const currency = new Intl.NumberFormat("ca-ES", {
    style: "currency",
    currency: "EUR",
  });

  return (
    <section className="panel catalog-panel">
      <div className="panel-heading catalog-heading">
        <div>
          <p className="eyebrow">PRODUCTES DESATS</p>
          <h2>Catàleg actual</h2>
        </div>
        <span className="badge">
          {catalog ? `${catalog.total} productes` : "Carregant…"}
        </span>
      </div>

      <div className="catalog-toolbar">
        <label className="catalog-search">
          Cerca per nom, ID d’Àgora o codi de barres
          <input
            type="search"
            value={query}
            placeholder="Ex.: tarot, 4039 o 843…"
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <p aria-live="polite">
          {loading
            ? "Actualitzant…"
            : `${catalog?.items.length ?? 0} resultats visibles`}
        </p>
      </div>

      {error && (
        <p className="alert error" role="alert">
          {error}
        </p>
      )}

      {!error && (
        <div className="table-wrap catalog-table-wrap" tabIndex={0}>
          <table>
            <thead>
              <tr>
                <th>ID Àgora</th>
                <th>Nom</th>
                <th>Família</th>
                <th>Codi de barres</th>
                <th>Preu</th>
              </tr>
            </thead>
            <tbody>
              {catalog?.items.map((product) => (
                <tr key={product.id}>
                  <td className="catalog-id">{product.sourceKey}</td>
                  <td>{product.name}</td>
                  <td>{product.family ?? "—"}</td>
                  <td className="catalog-barcode">{product.barcode ?? "—"}</td>
                  <td className="catalog-price">
                    {currency.format(product.priceCents / 100)}
                  </td>
                </tr>
              ))}
              {!loading && catalog?.items.length === 0 && (
                <tr>
                  <td className="catalog-empty" colSpan={5}>
                    No s'han trobat productes amb aquesta cerca.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Dashboard({
  user,
  onLogout,
}: {
  user: AuthenticatedUser;
  onLogout: () => void;
}) {
  const [catalogRefreshToken, setCatalogRefreshToken] = useState(0);
  const [section, setSection] = useState<
    "operations" | "sales" | "analysis" | "catalog" | "bridge" | "reports"
  >("operations");

  async function logout() {
    await fetch("/api/v1/auth/logout", {
      method: "POST",
      credentials: "include",
    });
    onLogout();
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <div>
          <p className="eyebrow">EIRA SEREN · FIRA TPV</p>
          <h1>Administració</h1>
        </div>
        <div className="user-actions">
          <BackendTag />
          <span>{user.displayName}</span>
          <button className="link-button" onClick={() => void logout()}>
            Tancar sessió
          </button>
        </div>
      </header>
      <main className="content">
        <nav className="section-nav" aria-label="Seccions d'administració">
          <button
            className={section === "bridge" ? "active" : ""}
            type="button"
            onClick={() => setSection("bridge")}
          >
            Bridge i Àgora
          </button>
          <button
            className={section === "operations" ? "active" : ""}
            type="button"
            onClick={() => setSection("operations")}
          >
            Fires i caixa
          </button>
          <button
            className={section === "sales" ? "active" : ""}
            type="button"
            onClick={() => setSection("sales")}
          >
            Vendes
          </button>
          <button
            className={section === "analysis" ? "active" : ""}
            type="button"
            onClick={() => setSection("analysis")}
          >
            Anàlisi de productes
          </button>
          <button
            className={section === "reports" ? "active" : ""}
            type="button"
            onClick={() => setSection("reports")}
          >
            Informes i impressió
          </button>
          <button
            className={section === "catalog" ? "active" : ""}
            type="button"
            onClick={() => setSection("catalog")}
          >
            Catàleg
          </button>
        </nav>
        <section className="welcome">
          <div>
            <p className="eyebrow">
              {section === "operations"
                ? "OPERACIÓ"
                : section === "sales"
                  ? "VENDES"
                  : section === "reports"
                    ? "INFORMES"
                    : section === "analysis"
                      ? "ANÀLISI"
                      : section === "bridge"
                        ? "BRIDGE"
                        : "CATÀLEG"}
            </p>
            <h2>
              {section === "operations"
                ? "Fires i caixa compartida"
                : section === "sales"
                  ? "Historial i transferència a Àgora"
                  : section === "reports"
                    ? "Informes de tiquets i productes agrupats"
                    : section === "analysis"
                      ? "Productes venuts per fira"
                      : section === "bridge"
                        ? "Diagnòstic segur de la connexió amb Àgora"
                        : "Catàleg i preparació operativa"}
            </h2>
          </div>
          <p>
            {section === "operations"
              ? "Crea la fira, obre cada jornada i controla el fons d’efectiu compartit."
              : section === "sales"
                ? "Consulta cada venda, les quantitats registrades i el seu estat de transferència."
                : section === "reports"
                  ? "Imprimeix les vendes sincronitzades d'una fira completa o de les jornades seleccionades."
                  : section === "analysis"
                    ? "Analitza unitats, imports i anul·lacions agrupades per producte."
                    : section === "bridge"
                      ? "Consulta el TPV i executa proves de lectura sense exposar el terminal ni modificar Àgora."
                      : "Valida el fitxer complet abans d’aplicar qualsevol canvi a la base de dades."}
          </p>
        </section>
        {section === "operations" ? (
          <OperationsPanel />
        ) : section === "sales" ? (
          <SalesPanel />
        ) : section === "analysis" ? (
          <ProductAnalysisPanel />
        ) : section === "reports" ? (
          <ReportsPanel />
        ) : section === "bridge" ? (
          <BridgeMaintenancePanel />
        ) : (
          <>
            <ImportPanel
              onApplied={() => setCatalogRefreshToken((current) => current + 1)}
            />
            <CatalogPanel refreshToken={catalogRefreshToken} />
          </>
        )}
      </main>
    </div>
  );
}

export function App() {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void api<MeResponse>("/api/v1/auth/me")
      .then((response) => setUser(response.user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="splash">Carregant Fira TPV…</div>;
  }

  return user ? (
    <Dashboard user={user} onLogout={() => setUser(null)} />
  ) : (
    <Login onLogin={setUser} />
  );
}
